import React from 'react';
import {
  Box, Paper, Button, Typography, Chip, LinearProgress, IconButton, Tooltip
} from '@mui/material';
import {
  Add as AddIcon, ArrowBack as ArrowBackIcon, ArrowForward as ArrowForwardIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const PageManager = ({ 
  currentPage, 
  setCurrentPage, 
  maxPage, 
  currentFields, 
  onDeletePage 
}) => {
  const { t } = useTranslation();

  return (
    <Paper sx={{ 
      p: 2, 
      mt: 2,
      borderRadius: 2,
      border: '1px solid #e2e8f0',
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white'
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Chip 
          label={`${currentFields.length} ${t('fields')}`} 
          size="small" 
          sx={{ 
            bgcolor: 'rgba(255,255,255,0.2)', 
            color: 'white',
            fontWeight: 500 
          }} 
        />
      </Box>
      
      {/* Progress Bar */}
      <Box sx={{ mb: 2 }}>
        <LinearProgress
          variant="determinate"
          value={Math.round((currentPage / maxPage) * 100)}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: 'rgba(255,255,255,0.2)',
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
              background: 'linear-gradient(90deg, #4facfe 0%, #00f2fe 100%)'
            }
          }}
        />
        <Typography align="center" sx={{ mt: 0.5, fontWeight: 500, fontSize: '0.875rem' }}>
          {`${t('step')} ${currentPage} / ${maxPage}`}
        </Typography>
      </Box>

      {/* Sayfa Yönetimi Butonları */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        gap: 2
      }}>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          sx={{ 
            minWidth: 100,
            bgcolor: 'rgba(255,255,255,0.15)',
            color: 'white',
            fontWeight: 600,
            borderRadius: 2,
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.25)'
            },
            '&:disabled': {
              bgcolor: 'rgba(255,255,255,0.1)',
              color: 'rgba(255,255,255,0.5)'
            }
          }}
          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
          disabled={currentPage === 1}
        >
          {t('back')}
        </Button>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{ 
              bgcolor: 'rgba(76, 175, 80, 0.9)',
              color: 'white',
              fontWeight: 600,
              borderRadius: 2,
              '&:hover': {
                bgcolor: 'rgba(76, 175, 80, 1)'
              }
            }}
            onClick={() => {
              const newPage = maxPage + 1;
              setCurrentPage(newPage);
            }}
          >
            {t('add_page')}
          </Button>

          {maxPage > 1 && (
            <Tooltip title={t('delete_page')} placement="top">
              <IconButton
                onClick={() => onDeletePage(currentPage)}
                sx={{
                  bgcolor: 'rgba(244, 67, 54, 0.9)',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(244, 67, 54, 1)'
                  }
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Button
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          sx={{ 
            minWidth: 100,
            bgcolor: 'rgba(255,255,255,0.15)',
            color: 'white',
            fontWeight: 600,
            borderRadius: 2,
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.25)'
            },
            '&:disabled': {
              bgcolor: 'rgba(255,255,255,0.1)',
              color: 'rgba(255,255,255,0.5)'
            }
          }}
          onClick={() => setCurrentPage(p => Math.min(maxPage, p + 1))}
          disabled={currentPage === maxPage}
        >
          {t('next')}
        </Button>
      </Box>
    </Paper>
  );
};

export default PageManager;
